/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import PropTypes from 'prop-types';
import { Info } from 'lucide-react';

const CLASS_TOGGLE_TOOLTIP =
  'tooltip tooltip-right z-[100] before:bg-[#fffdf2] before:text-slate-800 before:border before:border-amber-200 before:shadow-2xl before:rounded-xl before:px-3 before:py-2 before:font-medium before:text-xs before:max-w-none before:content-[attr(data-tip)] before:whitespace-nowrap after:border-r-[#fffdf2]';

/**
 * OTP Verification toggle + "Send OTP Via" select, shared by every page that lets a
 * user require OTP verification on an assignment (single create/edit, bulk upload).
 * Resets `via` back to 'email' whenever the toggle is switched off, so the channel
 * shown next time it's re-enabled is always in a known state.
 */
const OtpVerificationToggle = ({ toggleId, checked, onToggleChange, via, onViaChange, disabled, tooltip }) => {
  const handleToggle = (e) => {
    if (disabled) return;
    const isChecked = e.target.checked;
    onToggleChange(isChecked);
    if (!isChecked) onViaChange('email');
  };

  return (
    <>
      <div className="flex items-center justify-between px-4 py-3.5 bg-base-200/50 border border-base-300 rounded-xl">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em]">
            OTP Verification
          </span>
          {tooltip && (
            <div className={CLASS_TOGGLE_TOOLTIP} data-tip={tooltip}>
              <Info size={10} className="text-base-content/30 cursor-help" />
            </div>
          )}
        </div>
        <label className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
          <input
            id={toggleId}
            type="checkbox"
            className="sr-only peer"
            checked={checked}
            onChange={handleToggle}
            disabled={disabled}
          />
          <div className="relative w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary peer-disabled:opacity-50" />
        </label>
      </div>
      {checked && (
        <div className="flex items-center justify-between px-4 py-3.5 bg-base-200/50 border border-base-300 rounded-xl">
          <span className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em]">
            Send OTP Via
          </span>
          <select
            value={via}
            onChange={(e) => !disabled && onViaChange(e.target.value)}
            disabled={disabled}
            className="rounded-lg pl-3 pr-8 py-2 bg-base-100 border border-base-300 text-xs font-bold text-base-content focus:ring-2 focus:ring-primary focus:border-primary outline-none disabled:opacity-50 disabled:bg-base-200"
          >
            <option value="sms">SMS</option>
            <option value="email">Email</option>
          </select>
        </div>
      )}
    </>
  );
};

OtpVerificationToggle.propTypes = {
  toggleId: PropTypes.string,
  checked: PropTypes.bool.isRequired,
  onToggleChange: PropTypes.func.isRequired,
  via: PropTypes.oneOf(['sms', 'email']).isRequired,
  onViaChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  tooltip: PropTypes.string,
};

OtpVerificationToggle.defaultProps = {
  toggleId: undefined,
  disabled: false,
  tooltip: undefined,
};

export default OtpVerificationToggle;
